// Pure rules of panel R (Rollback) on the /cvc-migration page. jest cannot load
// .vue files, so every decision the panel makes lives here and the component only
// renders it. The server owns what is allowed (steps.reasons); this file says
// where the migration is, what an undo means there and what it does in order.

import {
  ROLLBACK_SECTION,
  ROLLBACK_TYPE,
  SHUT_TYPE,
  activityRows,
  inScope,
  isNewer,
  monitoredCount,
  newestEvent,
  plural,
  repointEvidence,
  repointedEarly,
  reservedRows,
  rollbackConfirmText,
  rolledBack,
  statusOf,
  stepButton,
  unremoved,
  windowShortText,
} from '@/utils/cvcMigrationPage';
import { POI_TIMEZONE, SYDNEY, ZONE_LABEL, formatDual, formatInZone, toDate } from '@/utils/zonedTime';

export { ROLLBACK_SECTION };

const MINUTE_MS = 60 * 1000;
const CRON_MINUTES = 5;

// The twenty-one points a migration can be at: the nineteen of release A, plus a
// rollback under way after the re-point confirm and one closed as rolled back.
const HIDDEN_BUTTON_POINTS = ['none', 'removalQueued', 'removed'];
const BEFORE_THE_SHUT = ['open', 'reserved', 'prebuilt', 'shutFailed'];
const AFTER_THE_ROLLBACK = ['rollbackQueued', 'rollbackFailed', 'rolledBack'];

const statics = (n) => `${n} ${plural(n, 'static', 'statics')}`;
const timeMs = (value) => {
  const date = toDate(value);
  return date ? date.getTime() : 0;
};
const newest = (rows, key) => rows.reduce((best, r) => (!best || timeMs(r[key]) > timeMs(best[key]) ? r : best), null);

// A Preflight from a server without the rollback block has no statics on a new IP.
const EMPTY_ROLLBACK = { liveCommitted: 0, radiusReadable: true, statics: [] };

function rollbackOf (preflight) {
  const given = (preflight && preflight.rollback) || {};
  return {
    liveCommitted: given.liveCommitted || 0,
    radiusReadable: given.radiusReadable !== false,
    statics: Array.isArray(given.statics) ? given.statics : EMPTY_ROLLBACK.statics,
  };
}

// Where the migration is. First rule that applies wins. A rollback after the
// confirm (under way or closed) comes first, whatever the forward stamps say. The
// source removal state comes from Usher's record: only a removal that is queued or
// done closes the way back, a failed one does not.
export function rollbackPoint (preflight, reconnect) {
  const record = preflight && preflight.record;
  const phase = preflight && preflight.steps && preflight.steps.phase;
  if (phase === 'rolledBackClosed') return 'closedRolledBack';
  if (phase === 'rollingBack') return 'rollingBack';
  if (!record) return 'none';
  if (phase === 'cancelled') return 'cancelled';
  if (phase === 'removed' || record.sourceRemoval === 'done') return 'removed';
  if (record.sourceRemoval === 'queued') return 'removalQueued';
  if (phase === 'migrated') return 'migrated';
  if (phase === 'activated') return 'activated';
  if (phase === 'repointConfirmed') return 'confirmed';
  if (phase === 'rollbackPending') return 'rollbackQueued';
  if (phase === 'rollbackFailed') return 'rollbackFailed';
  if (phase === 'waitingRepoint') {
    if (repointEvidence(preflight, reconnect).onTargetCount > 0) return 'repointSeen';
    return rollbackOf(preflight).liveCommitted > 0 ? 'committed' : 'shutDone';
  }
  if (phase === 'shutFailed') return 'shutFailed';
  if (repointedEarly(preflight, reconnect)) return 'earlyRepoint';
  if (phase === 'shutPending') return 'shutQueued';
  if (rolledBack(record)) return 'rolledBack';
  if (unremoved(record.targetCopies).length > 0) return 'prebuilt';
  if (reservedRows(preflight) > 0) return 'reserved';
  return 'open';
}

export function showRollbackButton (point) {
  return !HIDDEN_BUTTON_POINTS.includes(point);
}

// The next CVC cron run: the next five minute mark after now, as Sydney time.
export function nextCronMark (nowUtc) {
  const now = toDate(nowUtc);
  if (!now) return '';
  const step = CRON_MINUTES * MINUTE_MS;
  const mark = new Date((Math.floor(now.getTime() / step) + 1) * step);
  return formatInZone(mark, SYDNEY).split(' ')[1];
}

// "11:24 SYD (08:24 PER)": the Sydney clock and the POI clock when they differ.
function sydneyClock (utc, poiState) {
  const zone = POI_TIMEZONE[poiState];
  const sydney = formatInZone(utc, SYDNEY).split(' ')[1];
  if (!sydney) return '';
  const poi = zone && zone !== SYDNEY ? ` (${formatInZone(utc, zone).split(' ')[1]} ${ZONE_LABEL[zone]})` : '';
  return `${sydney} SYD${poi}`;
}

// Everything the texts of panel R are made of, read once from the Preflight and
// the last reconnection answer: counts, names, times and the rollback ledger.
export function rollbackContext (preflight, reconnect, nowUtc) {
  const p = preflight || {};
  const record = p.record || {};
  const rb = rollbackOf(p);
  const rows = p.rows || [];
  const poiState = p.poiState;
  const fmt = (utc) => formatDual(utc, poiState);
  const shutEvent = newestEvent(record, [SHUT_TYPE]);
  const backEvent = newestEvent(record, [ROLLBACK_TYPE]);
  const sourceCopies = record.sourceCopies || [];
  const shutCopies = unremoved(sourceCopies).filter(c => c.shutAt);
  const targetCopies = unremoved(record.targetCopies);
  const withNewIp = rows.filter(r => r.avcClass === 'VISP' && r.ipType === 'Static' && (r.hasPendingIp || r.committed)).length;
  const restoreTotal = Math.max(rb.liveCommitted, rb.statics.filter(s => s.state === 'committed' || s.state === 'restored').length);
  const evidence = repointEvidence(p, reconnect);
  const network = p.localNetwork || {};
  const removedAt = newest(sourceCopies.filter(c => c.removedAt), 'removedAt');
  const hasWindow = !!(record.cutoverWindowStart && record.cutoverWindowEnd);
  const shutDone = shutEvent && statusOf(shutEvent) === 'complete' ? shutEvent.updatedAt || shutEvent.createdAt : null;
  const backAfterShut = backEvent && (!shutEvent || isNewer(backEvent, shutEvent)) ? backEvent : null;
  return {
    cvc: p.cvcName,
    cvcId: p.cvcIdentifier,
    source: p.sourceHostname,
    target: p.targetHostname,
    state: poiState,
    poiState,
    nas: reconnect && reconnect.targetNasIp,
    local: network.customerName || network.bridgeDomain || 'the LOCAL network',
    nowUtc,
    record,
    fmt,
    hasWindow,
    window: hasWindow ? windowShortText(record.cutoverWindowStart, record.cutoverWindowEnd, poiState) : '',
    // counts
    c: rb.liveCommitted,
    n: Math.max(withNewIp, rb.liveCommitted),
    m: rows.filter(inScope).length,
    r: reservedRows(p),
    t: targetCopies.length,
    v: targetCopies.filter(c => c.ready).length,
    k: shutCopies.length,
    s: shutCopies.length,
    restoreTotal,
    held: record.rollbackPossible === true ? rb.statics.filter(s => !!s.oldIp).length : 0,
    // the rollback after the confirm: Usher's state and the statics put back since it started
    rollbackState: record.rollbackState || 'none',
    restoredSinceStart: rb.statics.filter(s => provenRestored(s) && timeMs(s.restoredAt) >= timeMs(record.rollbackStartedAt)).length,
    // the ledger and the reconnection evidence
    radiusReadable: rb.radiusReadable,
    ledger: rb.statics,
    onTarget: evidence.onTargetCount || 0,
    monitored: monitoredCount(reconnect),
    referenceUtc: reconnect && reconnect.referenceUtc,
    // events
    shutEvent,
    backEvent: backAfterShut,
    shutDone,
    removedAtUtc: removedAt ? removedAt.removedAt : null,
  };
}

const ALL_DOWN = (ctx) => `All ${ctx.m} services are down since ${ctx.fmt(ctx.shutDone)}. `
  + `They come back on ${ctx.source} within about 5 min of the rollback, plus the time their modem takes to redial.`;
// After the re-point confirm: going back is a booked Optus change, then a rollback started here.
const GOING_BACK = (ctx) => `Going back needs Optus to re-point ${ctx.cvc} back to ${ctx.source} (a booked change). `
  + `The tool prepares ${ctx.source} and puts the old IPs back at the start of that window.`;
const MOVES_USHER_BACK = (ctx) => ` After Optus confirms, the tool moves Usher back to ${ctx.source}.`;
export const CUSTOMERS_UNTIL_REPOINT_BACK = (ctx) => `Customers stay on ${ctx.target} until Optus re-points back. `
  + `At the re-point back every session drops and reconnects on ${ctx.source}.`;
// A rollback that was started and stopped: when, by whom, and the commit that puts the new IPs back.
const stoppedLine = (ctx) => {
  const record = ctx.record;
  if (ctx.rollbackState !== 'stopped') return '';
  const n = ctx.restoredSinceStart;
  const commit = n > 0
    ? ` Commit statics (section C) to give the ${n} restored ${plural(n, 'static', 'statics')} ${plural(n, 'its', 'their')} new IP again.`
    : '';
  return ` A rollback was started ${ctx.fmt(record.rollbackStartedAt)} and stopped ${ctx.fmt(record.rollbackStoppedAt)}${by(record.rollbackStoppedBy)}.${commit}`;
};
const unreadable = (ctx) => (ctx.radiusReadable === false && ctx.c > 0
  ? ` RADIUS cannot be read now, so the tool cannot tell which of the ${ctx.c} statics carry their new IP.`
  : '');
const by = (who) => (who ? ` by ${who}` : '');
// After a rollback, Optus re-pointed anyway when sessions are on the target: a cancel would remove the copies they are on.
const cancelHidden = (ctx) => ctx.onTarget > 0;
const reasonOf = (event) => (event && event.error) || 'no reason recorded';
const eventTime = (event) => (event ? event.updatedAt || event.createdAt : null);

// Where this migration is / What rolling back means now / What customers experience.
export function rollbackTexts (point, ctx) {
  const record = ctx.record;
  const states = {
    none: () => ({ where: 'No migration record yet.', means: 'Nothing to roll back.', customers: 'Not affected.' }),
    open: () => ({
      where: 'Record open. Nothing is built and no IP is reserved.',
      means: 'Close the record. Nothing on a router or in RADIUS changes.',
      customers: 'Not affected.',
    }),
    reserved: () => ({
      where: `${statics(ctx.r)} ${plural(ctx.r, 'has', 'have')} a reserved new IP. Nothing is built on ${ctx.target}.`,
      means: `Close the record and give the ${ctx.r} reserved IPs back to the ${ctx.state} pool, or keep them for the next attempt.`,
      customers: 'Not affected.',
    }),
    prebuilt: () => ({
      where: `${ctx.t} copies built on ${ctx.target} (${ctx.v} verified). ${ctx.source} carries the traffic.`,
      means: `Close the record, remove the ${ctx.t} copies from ${ctx.target} (CVC cron), and give back or keep the reserved IPs.`,
      customers: 'Not affected.',
    }),
    shutQueued: () => ({
      where: `Shut of ${ctx.source} queued ${ctx.fmt(ctx.shutEvent && ctx.shutEvent.createdAt)} (CVC cron). Customers are still on ${ctx.source}.`,
      means: 'A queued shut cannot be withdrawn. Rollback opens when the shut has run '
        + `(next CVC cron run at about ${nextCronMark(ctx.nowUtc)} SYD).`,
      customers: `Customers are still on ${ctx.source}. They drop when the shut runs and come back when a rollback runs, `
        + 'about 5 to 10 min later plus the time their modem takes to redial.',
    }),
    shutFailed: () => ({
      where: `${ctx.source} shut failed ${ctx.fmt(eventTime(ctx.shutEvent))}: ${reasonOf(ctx.shutEvent)}. Customers are still on ${ctx.source}.`,
      means: 'Nothing was recorded as shut. Retry the shut, or cancel.',
      customers: 'Not affected.',
    }),
    shutDone: () => ({
      where: `${ctx.source} is shut since ${ctx.fmt(ctx.shutDone)}. No static IP has changed.${unreadable(ctx)}`,
      means: `Bring ${ctx.source} back up (CVC cron). Customers come back on ${ctx.source} with the IP they had.`,
      customers: ALL_DOWN(ctx),
    }),
    committed: () => ({
      where: `${ctx.source} is shut since ${ctx.fmt(ctx.shutDone)}. ${ctx.c} of ${ctx.n} statics are committed to their new IP.${unreadable(ctx)}`,
      means: `Put the old IPs of ${statics(ctx.c)} back into RADIUS, then bring ${ctx.source} back up (CVC cron). `
        + `Customers come back on ${ctx.source} with their old IP.`,
      customers: `${ALL_DOWN(ctx)} ${ctx.c === 1 ? '1 static gets its old IP back.' : `${ctx.c} statics get their old IP back.`}`,
    }),
    repointSeen: () => ({
      where: `Optus appears to have re-pointed: ${ctx.onTarget} of ${ctx.monitored} services have a session on ${ctx.nas} `
        + `since ${ctx.fmt(ctx.referenceUtc)}. The re-point is not confirmed.${unreadable(ctx)}`,
      means: `Customers are already on ${ctx.target}. Bringing ${ctx.source} back up does not bring them back: `
        + 'that needs Optus to re-point back. '
        + `Recommended: confirm the re-point (section C) and fix forward on ${ctx.target}. `
        + `To go back to ${ctx.source} after all, confirm first, then start a rollback here.`,
      customers: `Services on ${ctx.nas} keep working on ${ctx.target}.`,
    }),
    earlyRepoint: () => ({
      where: `Optus appears to have re-pointed before the ${ctx.source} shut: ${ctx.onTarget} of ${ctx.monitored} services have a session on ${ctx.nas} `
        + `since ${ctx.fmt(ctx.referenceUtc)}. The re-point is not confirmed.`,
      means: `Do not cancel: that removes the ${ctx.target} copies these customers are on. `
        + `Go on in section C: the ${ctx.source} shut, Commit statics, then Confirm re-point.`,
      customers: `Customers are moving to ${ctx.target}: ${ctx.onTarget} services already have a session on ${ctx.nas}.`,
    }),
    rollbackQueued: () => ({
      where: `Rollback of ${ctx.source} queued ${ctx.fmt(ctx.backEvent && ctx.backEvent.createdAt)} (CVC cron).`,
      means: 'Wait for the CVC cron.',
      customers: ALL_DOWN(ctx),
    }),
    rollbackFailed: () => ({
      where: `Rollback of ${ctx.source} failed ${ctx.fmt(eventTime(ctx.backEvent))}: ${reasonOf(ctx.backEvent)}. ${ctx.source} is still shut.`,
      means: 'Retry the rollback. If Optus has re-pointed instead, confirm the re-point (section C).',
      customers: `All ${ctx.m} services stay down until the rollback works or Optus re-points.`,
    }),
    rolledBack: () => {
      const left = ctx.c;
      const back = `${ctx.source} was brought back up ${ctx.fmt(eventTime(ctx.backEvent))}. Customers are on ${ctx.source}.`;
      return left > 0
        ? {
          where: `${back} ${statics(left)} still ${plural(left, 'carries', 'carry')} ${plural(left, 'its', 'their')} new IP.`,
          means: `Restore the old IPs of ${statics(left)}, then shut again in a new window (section C)${cancelHidden(ctx) ? '.' : ' or cancel.'}`,
          customers: `${statics(left)} ${plural(left, 'is', 'are')} on ${ctx.source} with ${plural(left, 'its', 'their')} new IP `
            + 'and may not work until restored.',
        }
        : {
          where: back,
          means: `Nothing left to undo on ${ctx.source}. Shut again in a new window (section C)${cancelHidden(ctx) ? '.' : ', or cancel the migration.'}`,
          customers: `Customers are on ${ctx.source} with the IP they had.`,
        };
    },
    confirmed: () => ({
      where: `Optus re-pointed ${ctx.cvc} to ${ctx.target} (confirmed ${ctx.fmt(record.repointConfirmedAt)}${by(record.repointConfirmedBy)}). `
        + `Customers are on ${ctx.target}. Usher still points the CVC at ${ctx.source} (not activated).${stoppedLine(ctx)}`,
      means: GOING_BACK(ctx),
      customers: CUSTOMERS_UNTIL_REPOINT_BACK(ctx),
    }),
    activated: () => ({
      where: `Activated ${ctx.fmt(record.activatedAt)}: Usher points ${ctx.cvc} at ${ctx.target} and its VISP AVCs at ${ctx.local}. `
        + `Customers are on ${ctx.target}.${stoppedLine(ctx)}`,
      means: GOING_BACK(ctx) + MOVES_USHER_BACK(ctx),
      customers: CUSTOMERS_UNTIL_REPOINT_BACK(ctx),
    }),
    migrated: () => ({
      where: `Marked migrated ${ctx.fmt(record.completedAt)}. The ${ctx.s} copies on ${ctx.source} are still there, shut, `
        + `until ${removableFrom(ctx)}.${stoppedLine(ctx)}`,
      means: GOING_BACK(ctx) + MOVES_USHER_BACK(ctx),
      customers: CUSTOMERS_UNTIL_REPOINT_BACK(ctx),
    }),
    removalQueued: () => ({
      where: `Removal of the ${ctx.source} copies is queued.`,
      means: 'Once it runs, rollback is no longer possible. A queued removal cannot be withdrawn.',
      customers: 'Not affected by this page.',
    }),
    removed: () => {
      const when = ctx.fmt(ctx.removedAtUtc);
      return {
        where: `Source copies removed from ${ctx.source}${when ? ` ${when}` : ''}.`,
        means: `The ${ctx.source} copies of ${ctx.cvc} were removed${when ? ` ${when}` : ''}. The page cannot move this CVC back. `
          + `Going back would be a new move to ${ctx.source}: network and dev.`,
        customers: 'Not affected by this page.',
      };
    },
    cancelled: () => ({
      where: `Migration cancelled ${ctx.fmt(record.cancelledAt)}${by(record.cancelledBy)}.`,
      means: 'Nothing to roll back.',
      customers: 'Not affected.',
    }),
  };
  return (states[point] || states.none)();
}

function removableFrom (ctx) {
  const from = ctx.record.sourceRemovableFrom;
  return from ? ctx.fmt(from) : 'removed';
}

const cancelClosed = (ctx) => `Cancel is not offered while sessions are on ${ctx.nas}.`;

// What cannot be undone at this point (hidden when empty).
export function rollbackNotPossible (point, ctx) {
  if (point === 'shutFailed') {
    return [`Nothing was recorded as shut on ${ctx.source}. If a network engineer sees shut subinterfaces of ${ctx.cvc} on ${ctx.source}, `
      + 'they bring them up by hand.'];
  }
  if (point === 'earlyRepoint' || (point === 'rolledBack' && cancelHidden(ctx))) return [cancelClosed(ctx)];
  if ((point === 'activated' || point === 'migrated') && ctx.held > 0) {
    return [`${ctx.held} old IPs held until the ${ctx.source} copies are removed.`];
  }
  return [];
}

const step = (key, label, system, duration, state) => ({ key, label, system, duration, state });
const cronSystem = (host) => `Tool: Usher job on ${host} (CVC cron, every 5 min)`;

// Done {t} by {who} / Failed {t}: {reason} / Running / To do for the router step.
function bringUpState (ctx) {
  const event = ctx.backEvent;
  if (!event) return 'To do';
  const status = statusOf(event);
  if (status === 'pending') return 'Running';
  if (status === 'complete') return `Done ${ctx.fmt(eventTime(event))}${by(event.createdBy)}`;
  if (status === 'failed') return `Failed ${ctx.fmt(eventTime(event))}: ${reasonOf(event)}`;
  return 'To do';
}

// Done once nothing is left on a new IP and a row was restored; failed when a
// rollback is under way and statics are still on theirs.
function restoreState (ctx, point) {
  if (ctx.c === 0) {
    const restored = newest(ctx.ledger.filter(s => s.restoredAt), 'restoredAt');
    return restored ? `Done ${ctx.fmt(restored.restoredAt)}${by(restored.restoredBy)}` : 'To do';
  }
  if (!AFTER_THE_ROLLBACK.includes(point)) return 'To do';
  const rows = ctx.ledger.filter(s => s.state === 'committed' || s.state === 'unknown');
  const last = newest(rows, 'lastOutcomeAt');
  const at = last && last.lastOutcomeAt ? last.lastOutcomeAt : eventTime(ctx.backEvent);
  return `Failed ${ctx.fmt(at)}: ${ctx.c} not restored`;
}

function undoBeforeShut (ctx, point) {
  const steps = [step('close', 'Close the record', 'Tool: Usher data', 'instant', point === 'cancelled'
    ? `Done ${ctx.fmt(ctx.record.cancelledAt)}${by(ctx.record.cancelledBy)}`
    : 'To do')];
  if (ctx.t > 0) {
    steps.push(step('removeTarget', `Remove ${ctx.t} target copies from ${ctx.target}`, cronSystem(ctx.target), 'up to 5 min', 'To do'));
  }
  if (ctx.r > 0) steps.push(step('release', `Release ${ctx.r} reserved IPs`, 'Tool: VISP pool', 'instant', 'To do'));
  if (ctx.hasWindow && point !== 'cancelled') steps.push(step('optusBooking', 'Cancel the Optus booking', 'You: Optus', '', ''));
  return steps;
}

// The steps of the undo for the point, in the order they run, each with the system
// that does it, how long it takes and where it stands.
export function rollbackSteps (point, ctx) {
  if (BEFORE_THE_SHUT.includes(point) || point === 'cancelled') return undoBeforeShut(ctx, point);
  const restoring = point === 'committed' || (point === 'repointSeen' && ctx.c > 0);
  const restoreCount = restoring ? ctx.c : ctx.restoreTotal;
  const goingBack = restoring || point === 'shutDone' || point === 'repointSeen' || AFTER_THE_ROLLBACK.includes(point);
  if (!goingBack) return [];
  const steps = [];
  if (restoreCount > 0 && (restoring || AFTER_THE_ROLLBACK.includes(point))) {
    const seconds = Math.max(2, Math.ceil(restoreCount / 2));
    steps.push(step('restore', `Old IPs back into RADIUS (${restoreCount})`, 'Tool: RADIUS and VISP pool', `about ${seconds} s`, restoreState(ctx, point)));
  }
  steps.push(step('bringUp', `Bring ${ctx.source} up (${ctx.k})`, cronSystem(ctx.source), 'up to 5 min', bringUpState(ctx)));
  steps.push(step('tellOptus', 'Tell Optus not to re-point', 'You: Optus', '', ''));
  return steps;
}

const stopOptus = (ctx) => ({
  key: 'stopOptus',
  title: 'Tell Optus not to re-point',
  text: `${ctx.cvc} (${ctx.cvcId}): do not re-point. The window is rolled back.`,
});

const ROLLED_BACK_TITLE = 'Pass this on: the window was rolled back.';
const rolledBackNotice = (ctx) => ({
  title: ROLLED_BACK_TITLE,
  message: `${ctx.cvc} (${ctx.cvcId}): window rolled back ${sydneyClock(eventTime(ctx.backEvent), ctx.poiState)}. `
    + 'Static IPs stay as they are now; the IP change moves to the next window.',
});

// Tasks for people outside the tool, each with the text to copy.
export function rollbackOutside (point, ctx) {
  if (BEFORE_THE_SHUT.includes(point)) {
    if (!ctx.hasWindow) return [];
    const lines = [{
      key: 'optusBooking',
      title: 'Cancel the Optus booking',
      text: `Cancel the Optus booking for ${ctx.cvc} (${ctx.cvcId}), window ${ctx.window}.`,
    }];
    if (ctx.r > 0) {
      lines.push({ key: 'wholesalers', title: 'Tell the wholesalers', text: `Tell the wholesalers the IP change on ${ctx.window} is off.` });
    }
    return lines;
  }
  if (['shutDone', 'committed', 'repointSeen', 'rollbackQueued', 'rollbackFailed'].includes(point)) return [stopOptus(ctx)];
  if (point === 'rolledBack') {
    const notice = rolledBackNotice(ctx);
    return [{ key: 'rolledBack', title: notice.title, text: notice.message }];
  }
  return [];
}

// The "pass this on" notice Section C shows once a rollback has completed.
export function rollbackNotice (preflight) {
  if (rollbackPoint(preflight, null) !== 'rolledBack') return null;
  return rolledBackNotice(rollbackContext(preflight, null, null));
}

// Every rollback action of the record, newest first: the rollback events, the
// restore runs of the ledger and the cancel.
export function rollbackHistory (record, poiState, nowUtc, statics) {
  return activityRows(record, poiState, nowUtc, statics).filter(r => r.kind === 'rollback' || r.kind === 'cancel');
}

// The grey line sections C and E show where their undo buttons used to be.
export const ROLLBACK_POINTER = 'To roll back, use section R (Rollback).';

// ---- actions ----

// One button of panel R: enabled only when the server says so (`flag`), with the
// server reason when it does not.
export const action = (steps, key, label, flag, extra = {}) => ({ key, label, flag, ...stepButton(steps, flag), ...extra });

const plus = (n, one, many) => `${n} ${plural(n, one, many)}`;

// VispApi refuses a rollback it cannot check when RADIUS is unreachable (the
// reason on canRollback and the 409 text both start like this). It is the only
// case in which bringing the source back without the restore is offered.
const RADIUS_REFUSAL = 'RADIUS database not reachable from VispApi: cannot check';

export function isRadiusRefusal (steps, ctx) {
  const reason = steps && steps.canRollback !== true && steps.reasons && steps.reasons.canRollback;
  return typeof reason === 'string' && reason.startsWith(RADIUS_REFUSAL) && ctx.radiusReadable === false && ctx.c > 0;
}

// The buttons for the point. Cancel, the rollback with or without a restore, the
// restore on its own, the pointer to the shut in section C, and the leftovers of
// a cancelled migration.
export function rollbackActions (point, steps, ctx) {
  const cancel = () => action(steps, 'cancel', 'Cancel migration...', 'canCancel', { color: 'error', outlined: true });
  const rollback = () => {
    const withIps = ctx.c > 0;
    let label = `Roll back: bring ${ctx.source} up...`;
    if (withIps) label = `Roll back: restore ${plus(ctx.c, 'old IP', 'old IPs')} and bring ${ctx.source} up...`;
    if (point === 'rollbackFailed') label = 'Retry rollback';
    return action(steps, 'rollback', label, 'canRollback', { kind: withIps ? 'restoreAndBringUp' : 'bringUp', color: 'error' });
  };
  const restore = () => action(steps, 'restore', `Restore old IPs (${ctx.c})`, 'canRestore', { kind: 'restore', color: 'error' });
  // Enabled on purpose: the refusal is the reason the rollback is closed, and the
  // server still checks everything else.
  const unshutOnly = () => ({
    key: 'unshutOnly',
    kind: 'unshutOnly',
    label: `Bring ${ctx.source} back without restoring IPs...`,
    flag: 'canRollback',
    disabled: false,
    reason: '',
    color: 'error',
    outlined: true,
  });
  switch (point) {
    case 'open':
    case 'reserved':
    case 'prebuilt':
      return [cancel()];
    case 'shutFailed':
      return [{ key: 'retryShut', label: 'Retry the shut (section C)', flag: null, disabled: false, reason: '' }, cancel()];
    case 'shutDone':
    case 'committed':
    case 'repointSeen':
    case 'rollbackFailed':
      return isRadiusRefusal(steps, ctx) ? [rollback(), unshutOnly()] : [rollback()];
    case 'rollbackQueued':
      return ctx.c > 0 ? [restore()] : [];
    case 'rolledBack':
      // Optus re-pointed anyway: a cancel would remove the target copies those customers are on.
      return (ctx.c > 0 ? [restore()] : []).concat(cancelHidden(ctx) ? [] : [cancel()]);
    case 'cancelled': {
      const left = [];
      if (ctx.t > 0) left.push(action(steps, 'removeTarget', `Remove target copies (${ctx.t})`, 'canRemoveTargetCopies'));
      if (ctx.r > 0) left.push(action(steps, 'retryRelease', 'Retry release', 'canRelease'));
      return left;
    }
    default:
      return [];
  }
}

// ---- dialogs ----

const ipChange = (s) => `${s.username} ${s.newIp} -> ${s.oldIp}`;
const onNewIp = (ctx) => ctx.ledger.filter(s => s.state === 'committed');
// With RADIUS unreadable: every static the restore cannot prove is back on its old IP.
const mayBeOnNewIp = (ctx) => ctx.ledger.filter(s => !provenRestored(s));

// Optus may have re-pointed while the source was shut: the rollback still works,
// but those services stay where they are.
const repointAlerts = (ctx) => (ctx.onTarget > 0
  ? [{
    type: 'error',
    text: `${ctx.onTarget} of ${ctx.monitored} services already have a session on ${ctx.nas}: Optus appears to have re-pointed. `
      + `Rolling back brings ${ctx.source} back up, but these services stay on ${ctx.nas} with the IP they have now. `
      + 'Confirm the re-point instead.',
  }]
  : []);

// The three confirmations, by impact: D2 bring the source up (a tick), D3 restore
// the old IPs and bring the source up (a tick and the typed CVC name), D4 restore
// the old IPs on their own (the typed CVC name).
export function rollbackDialog (kind, preflight, reconnect) {
  const ctx = rollbackContext(preflight, reconnect, null);
  const tick = {
    label: `Optus has not re-pointed ${ctx.cvc} and knows not to (or was never told)`,
    shown: true,
    required: true,
  };
  if (kind === 'restore') {
    return {
      title: `Restore the old IPs of ${plus(ctx.c, 'static', 'statics')}`,
      body: [`Writes the old IP back into RADIUS for ${plus(ctx.c, 'static', 'statics')} that still ${plural(ctx.c, 'carries its', 'carry their')} new IP. No router change.`],
      consequences: onNewIp(ctx).map(ipChange),
      alerts: [],
      proceedLabel: 'Restore old IPs',
      requireTyping: true,
    };
  }
  if (kind === 'unshutOnly') {
    const rows = mayBeOnNewIp(ctx);
    const n = rows.length || ctx.c;
    const k = ctx.k;
    return {
      title: `Bring ${ctx.source} back without restoring IPs`,
      body: [
        'RADIUS cannot be read, so the tool cannot put the old IPs back. '
          + `This brings the ${plus(k, 'subinterface', 'subinterfaces')} of ${ctx.cvc} on ${ctx.source} back up without restoring any IP (CVC cron, about 5 min).`,
        `${plus(n, 'static', 'statics')} may come back on ${ctx.source} with the new IP ${plural(n, 'it was', 'they were')} given for ${ctx.target}. `
          + `${plural(n, 'It will work slowly until its old IP is', 'They will work slowly until their old IPs are')} restored. `
          + `Restore ${plural(n, 'it', 'them')} as soon as RADIUS can be read.`,
      ],
      consequences: rows.map(ipChange),
      alerts: repointAlerts(ctx),
      checkbox: { label: 'I understand these customers may be on the wrong IP until fixed', shown: true, required: true },
      proceedLabel: `Bring ${ctx.source} back up`,
      requireTyping: true,
    };
  }
  if (kind === 'restoreAndBringUp') {
    return {
      title: `Roll back ${ctx.cvc}: restore ${plus(ctx.c, 'old IP', 'old IPs')} and bring ${ctx.source} up`,
      body: [rollbackConfirmText(preflight, ctx.c)],
      consequences: onNewIp(ctx).map(ipChange),
      alerts: repointAlerts(ctx),
      checkbox: tick,
      proceedLabel: 'Restore IPs and roll back',
      requireTyping: true,
    };
  }
  return {
    title: `Roll back ${ctx.cvc}: bring ${ctx.source} up`,
    body: [
      rollbackConfirmText(preflight),
      `Customers come back on ${ctx.source} after the CVC cron runs (every 5 min) and their modem redials.`,
    ],
    consequences: [],
    alerts: repointAlerts(ctx),
    checkbox: tick,
    proceedLabel: 'Roll back',
    requireTyping: false,
  };
}

// ---- results ----

// A static restored (RADIUS holds the old IP after the last commit). A registration after
// the restore is a new commit attempt, even if its result never reached the ledger.
const provenRestored = (s) => !!s.restoredAt
  && (!s.committedAt || timeMs(s.restoredAt) >= timeMs(s.committedAt))
  && !(s.registeredAt && timeMs(s.registeredAt) > timeMs(s.restoredAt));

// One row per static a restore has run for, failures first, from the ledger: the
// same after a reload and for a second operator.
export function restoreResultRows (preflight) {
  const tried = rollbackOf(preflight).statics.filter(s => s.restoredAt
    || (!!s.committedAt && timeMs(s.lastOutcomeAt) > timeMs(s.committedAt)));
  const rows = tried.map(s => {
    const restored = s.state === 'restored' || (s.state === 'unknown' && provenRestored(s));
    return {
      avcId: s.avcId,
      user: s.username,
      change: `${s.newIp} -> ${s.oldIp}`,
      restored,
      tone: restored ? 'green' : 'red',
      outcome: s.lastOutcome,
    };
  });
  return rows.filter(r => !r.restored).concat(rows.filter(r => r.restored));
}

const OUTCOME_ORDER = { failed: 0, done: 1, skipped: 2 };
const OUTCOME_TONE = { failed: 'red', done: 'green', skipped: 'grey' };
const outcomeRank = (outcome) => (OUTCOME_ORDER[outcome] === undefined ? 3 : OUTCOME_ORDER[outcome]);

// The per-static rows of a rollback or restore answer, failures first.
function answerRows (restore) {
  return ((restore && restore.results) || [])
    .map(r => ({
      avcId: r.avcId,
      radiusServiceId: r.radiusServiceId,
      outcome: r.outcome,
      message: r.message,
      tone: OUTCOME_TONE[r.outcome] || 'grey',
    }))
    .sort((a, b) => outcomeRank(a.outcome) - outcomeRank(b.outcome));
}

// The statics the rollback left on whatever IP they have (the override that
// brings the source back without the restore): the list the operator retries.
function notRestoredRows (answer) {
  return ((answer && answer.notRestored) || []).map(r => ({
    avcId: r.avcId,
    radiusServiceId: r.radiusServiceId,
    newIp: r.newIp,
    oldIp: r.oldIp,
    change: `${r.newIp} -> ${r.oldIp}`,
  }));
}

// What the page shows under the buttons once the rollback (or the restore) answered.
export function rollbackAnswerView (answer) {
  const restore = answer && answer.restore;
  const notRestored = notRestoredRows(answer);
  return {
    type: (restore && restore.failed > 0) || notRestored.length > 0 ? 'warning' : 'success',
    text: (answer && answer.message) || '',
    rows: answerRows(restore),
    notRestored,
  };
}

// A 409 after a restore ran (Usher refused the un-shut) still carries the restore
// rows: nothing the restore did may be hidden.
export function rollbackRefusalRows (error) {
  const response = error && error.response;
  if (!response || response.status !== 409) return [];
  const body = response.data;
  return answerRows(body && typeof body === 'object' && body.data ? body.data.restore : null);
}
